import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

type HandshakeStatus = 'idle' | 'waiting-for-ready' | 'authorized' | 'error';

interface UseIframeHandshakeResult {
    status: HandshakeStatus;
    targetOrigin: string | null;
    iframeRef: RefObject<HTMLIFrameElement | null>;
}

// Normalize the iframe URL and reject anything outside HTTP/S to prevent leaking tokens to unsupported schemes.
function getOriginSafe(url: string | undefined): string | null {
    if (!url) {
        return null;
    }
    try {
        const parsed = new URL(url);
        if (!['https:', 'http:'].includes(parsed.protocol)) {
            console.warn('Host container: blocked iframe origin due to unsupported protocol', parsed.protocol);
            return null;
        }
        return parsed.origin;
    } catch (error) {
        console.warn('Host container: failed to parse embed URL', { url, error });
        return null;
    }
}

export function useIframeHandshake(url: string, token: string | null): UseIframeHandshakeResult {
    // Memoize the validated origin so the handshake only reacts when the URL actually changes.
    const targetOrigin = useMemo(() => getOriginSafe(url), [url]);
    const [status, setStatus] = useState<HandshakeStatus>('idle');
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    // Push the identity token into the iframe once it has proven it is ready to receive it.
    const sendAuthMessage = useCallback(async () => {
        try {
            if (!token) {
                throw new Error('Identity token unavailable');
            }

            if (!targetOrigin) {
                return;
            }

            const frameWindow = iframeRef.current?.contentWindow;
            if (!frameWindow) {
                return;
            }

            frameWindow.postMessage({ type: 'AUTH', token }, targetOrigin);

            setStatus('authorized');
        } catch (authError) {
            console.error('Host container: failed to provide identity token to iframe', authError);
            setStatus('error');
        }
    }, [token, targetOrigin]);

    useEffect(() => {
        if (!targetOrigin || !token) {
            setStatus('idle');
            return;
        }

        // Accept READY messages only from the targeted iframe window and reply with credentials.
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== targetOrigin) {
                return;
            }

            if (event.source !== iframeRef.current?.contentWindow) {
                return;
            }

            const payload = event.data
            if (!payload || typeof payload !== 'object') {
                return;
            }

            const { type } = payload as { type?: unknown }
            if (type !== 'READY') {
                return;
            }

            sendAuthMessage();
        }

        window.addEventListener('message', handleMessage);
        setStatus('waiting-for-ready');

        return () => {
            window.removeEventListener('message', handleMessage);
        }
    }, [targetOrigin, token, sendAuthMessage]);

    return {
        status,
        targetOrigin,
        iframeRef
    }
}
